import * as helios from "@hyperionbt/helios";
import {
  Artwork,
  CardanoNetwork,
  FileHandle,
  KreateDatum,
  KreateUTxO,
} from "kreate-common/modules/business-types";
import { MetadataEnv } from "kreate-common/modules/metadata-env/types";
import { resolveArtworkMetadata } from "kreate-common/modules/metadata-env/utils";

import paramsMainnet from "./data/cardano-mainnet.json";
import paramsPreprod from "./data/cardano-preprod.json";
import paramsPreview from "./data/cardano-preview.json";
import { HeliosMetadata } from "./types";

// TODO: Regularly update the network params
// https://www.hyperion-bt.org/helios-book/api/reference/networkparams.html
// TODO: Precache `NetworkParams` if these inits are actually slow hence bad UX.
export function getCardanoNetworkParams(
  network: CardanoNetwork
): helios.NetworkParams {
  switch (network) {
    case "Mainnet":
      return new helios.NetworkParams(paramsMainnet);
    case "Preprod":
      return new helios.NetworkParams(paramsPreprod);
    case "Preview":
    default:
      return new helios.NetworkParams(paramsPreview);
  }
}

export function toHeliosUTxO(utxo: KreateUTxO): helios.UTxO {
  return new helios.UTxO(
    new helios.TxId(utxo.txId),
    BigInt(utxo.txIx),
    new helios.TxOutput(
      new helios.Address(utxo.address),
      new helios.Value(
        BigInt(utxo.lovelaceValue),
        new helios.Assets(
          utxo.otherAssets.map(({ mintingPolicyHash, tokens }) => [
            mintingPolicyHash,
            tokens.map(({ tokenName, tokenValue }) => [
              helios.textToBytes(tokenName),
              tokenValue,
            ]),
          ])
        )
      ),
      toHeliosDatum(utxo.datum)
    )
  );
}

export function toHeliosDatum(datum: KreateDatum): helios.Datum {
  if (datum.type === "protocol-params") {
    return helios.Datum.inline(
      new helios.ListData([
        new helios.PubKeyHash(datum.value.modPKH)._toUplcData(),
        new helios.Address(datum.value.treasuryAddress)._toUplcData(),
        new helios.IntData(BigInt(datum.value.kreatorMintFee)),
      ])
    );
  } else if (datum.type === "artwork") {
    return helios.Datum.inline(
      new helios.ListData([
        new helios.Address(datum.value.ownerAddress)._toUplcData(),
        // Collection Ids are MongoDB (hexstring) ObjectIds
        new helios.ByteArray(
          helios.hexToBytes(datum.value.collectionId)
        )._toUplcData(),
        // Token names are Artwork Ids which are MongoDB (hexstring) ObjectIds
        new helios.ByteArray(
          helios.textToBytes(datum.value.tokenName)
        )._toUplcData(),
        new helios.IntData(BigInt(datum.value.mintFee)),
        new helios.IntData(BigInt(datum.value.numEditions)),
        new helios.IntData(BigInt(datum.value.numMinted)),
      ])
    );
  } else if (datum.type === "marketplace") {
    return helios.Datum.inline(
      new helios.ListData([
        new helios.Address(datum.value.sellerAddress)._toUplcData(),
        new helios.AssetClass(
          `${datum.value.assetId.substring(
            0,
            56
          )}.${datum.value.assetId.substring(56)}`
        )._toUplcData(),
        new helios.IntData(BigInt(datum.value.editionPrice)),
      ])
    );
  } else if (datum.type === "secondary") {
    return helios.Datum.inline(
      new helios.ListData([
        new helios.Address(datum.value.sellerAddress)._toUplcData(),
        new helios.AssetClass(
          `${datum.value.assetId.substring(
            0,
            56
          )}.${datum.value.assetId.substring(56)}`
        )._toUplcData(),
        new helios.IntData(BigInt(datum.value.editionPrice)),
        datum.value.artistAddress != null
          ? new helios.ConstrData(0, [
              new helios.Address(datum.value.artistAddress)._toUplcData(),
            ])
          : new helios.ConstrData(1, []),
        new helios.IntData(BigInt(datum.value.editionRoyalty)),
      ])
    );
  } else if (datum.type === "offer") {
    return helios.Datum.inline(
      new helios.ListData([
        new helios.Address(datum.value.offererAddress)._toUplcData(),
        new helios.ByteArray(datum.value.assetId)._toUplcData(),
        new helios.IntData(BigInt(datum.value.editionPrice)),
        new helios.IntData(BigInt(datum.value.wantedQuantity)),
        helios.UplcBool.new(datum.value.isSecondary).data,
        datum.value.artistAddress != null
          ? new helios.ConstrData(0, [
              new helios.Address(datum.value.artistAddress)._toUplcData(),
            ])
          : new helios.ConstrData(1, []),
        new helios.IntData(BigInt(datum.value.editionRoyalty)),
      ])
    );
  } else if (datum.type === "auction-english") {
    return helios.Datum.inline(
      new helios.ListData([
        new helios.Address(datum.value.ownerAddress)._toUplcData(),
        new helios.IntData(BigInt(datum.value.deadline)),
        new helios.IntData(BigInt(datum.value.reserve)),
        new helios.IntData(BigInt(datum.value.increment)),
        new helios.Address(datum.value.bidderAddress)._toUplcData(),
      ])
    );
  }
  return helios.Datum.inline(new helios.ConstrData(0, []));
}

export function toHeliosAddress(
  network: CardanoNetwork,
  address: any
): helios.Address {
  return helios.Address.fromHashes(
    address.fields[0].constructor === 0
      ? new helios.PubKeyHash(address.fields[0].fields[0].bytes)
      : new helios.ValidatorHash(address.fields[0].fields[0].bytes),
    address.fields[1].fields[0].fields[0].constructor === 0
      ? new helios.StakeKeyHash(
          address.fields[1].fields[0].fields[0].fields[0].bytes
        )
      : new helios.StakingValidatorHash(
          address.fields[1].fields[0].fields[0].fields[0].bytes
        ),
    network !== "Mainnet"
  );
}

export function getHeliosMetadata({
  policyId,
  tokenName,
  name,
  description,
  image,
  files,
  otherFields,
}: {
  policyId: string;
  tokenName: string;
  name: string;
  description?: string;
  image: { src: string; mediaType: string };
  files: { src: string; mediaType: string; name?: string }[];
  otherFields: Record<string, string | number> | undefined;
}): HeliosMetadata {
  const dict = (obj: Record<string, HeliosMetadata>) =>
    ({ map: Object.entries(obj) } satisfies HeliosMetadata);

  return dict({
    [policyId]: dict({
      [tokenName]: dict({
        name: name,
        ...(description ? { description } : undefined),
        image: image.src,
        mediaType: image.mediaType,
        files: files.map((file) =>
          dict({
            src: file.src,
            ...(file.name ? { name: file.name } : undefined),
            mediaType: file.mediaType,
          })
        ),
        ...otherFields,
      }),
    }),
  });
}

function assert(condition: unknown, message?: string): asserts condition {
  if (!condition) throw new Error(message);
}

const GUMBALL_FILES = [
  {
    src: "ipfs://QmRXQ8D9N6QCwn4uj1kEVMkU6mFgJHxJF1jTZjhn7XhUMK",
    name: "Gumball (zar × sma11world)",
    mediaType: "image/gif",
  },
  {
    src: "ipfs://QmP61TvRWuu2fCaY7WeUXK4KprZ7jP9YsFMTma58Cs9RoF",
    name: "visualizer",
    mediaType: "video/quicktime",
  },
  {
    src: "ipfs://Qmc8vsNVaPucnSt3WTRdT2EheCoFEBBUa5wqc12tM287u4",
    name: "image",
    mediaType: "image/png",
  },
];

function toUrlEncoded(text: string) {
  return (
    text
      // 1. Split to tokens by zero-length separator
      .split(/\b/)
      // 2. Encode only necessary tokens
      .map((token) =>
        // eslint-disable-next-line no-control-regex
        /[\x00-\x1F]/.test(token) ||
        token.includes("%") ||
        // NOTE: pool.pm is prone to XSS, that's why we have to escape quotation marks
        token.includes("'") ||
        token.includes('"')
          ? encodeURIComponent(token)
          : token
      )
      // 3. Join everything
      .join("")
  );
}

export function toHeliosMetadataV2({
  policyId,
  tokenName,
  artwork,
  metadataEnv,
  fileIdToText,
}: {
  policyId: string;
  tokenName: string;
  artwork: Artwork;
  metadataEnv: MetadataEnv;
  fileIdToText: Record<string, string>;
}) {
  assert(
    /^[0-9a-f]{56}$/.test(policyId),
    "invalid policyId: " + JSON.stringify({ policyId })
  );
  assert(
    helios.textToBytes(artwork.title).length <= 64,
    "artwork title too long"
  );

  // NOTE: @kien-kreate: Choosing image for CIP-25:
  // - Priority: `artwork.imageFile` > `artwork.file`
  // - Eligibility: `contentType` must be "image/*"
  // https://github.com/cardano-foundation/CIPs/tree/master/CIP-0025
  const [image_ipfsHash, image_mediaType] =
    artwork.imageFile && artwork.imageFile.ipfsHash
      ? [artwork.imageFile.ipfsHash, artwork.imageFile.blob.contentType]
      : artwork.file.blob.contentType.startsWith("image/")
      ? [artwork.file.ipfsHash, artwork.file.blob.contentType]
      : [undefined, undefined];

  assert(
    image_ipfsHash?.length === 46 &&
      image_mediaType &&
      image_mediaType.startsWith("image/"),
    "invalid image: " + JSON.stringify({ image_ipfsHash, image_mediaType })
  );

  const formatFile = (file: FileHandle) => {
    const storageType =
      file.blob.contentType !== "text/html" || !fileIdToText[file.fileId]
        ? "ipfs"
        : "data-urlencoded";
    switch (storageType) {
      case "ipfs": {
        const file_ipfsHash = artwork.file.ipfsHash;
        const file_mediaType = artwork.file.blob.contentType;
        assert(
          file_ipfsHash?.length === 46,
          "invalid file: " +
            JSON.stringify({ file_ipfsHash, file_mediaType, storageType })
        );
        return {
          src: `ipfs://${file_ipfsHash}`,
          name: artwork.title,
          mediaType: file_mediaType,
        };
      }
      case "data-urlencoded": {
        const file_text = fileIdToText[file.fileId];
        const file_mediaType = file.blob.contentType;
        assert(
          file_text != null && file_mediaType.startsWith("text/"),
          "invalid file: " +
            JSON.stringify({ file_text, file_mediaType, storageType })
        );
        return {
          mediaType: file_mediaType,
          name: artwork.title,
          src: "data:text/html," + toUrlEncoded(file_text),
        };
      }
    }
  };

  const resolvedArtworkMetadata = artwork.metadata
    ? resolveArtworkMetadata(artwork.metadata, { metadataEnv })
    : undefined;

  const result = getHeliosMetadata({
    policyId,
    tokenName,
    name: artwork.title,
    description: artwork.description,
    image: {
      src: `ipfs://${image_ipfsHash}`,
      mediaType: image_mediaType,
    },
    files:
      artwork.artworkId === "649eecfbfa8432265758481b"
        ? GUMBALL_FILES
        : [formatFile(artwork.file)],
    otherFields: resolvedArtworkMetadata
      ? Object.fromEntries(
          resolvedArtworkMetadata.entries.map((e) => [e.key, e.value])
        )
      : undefined,
  });

  return result;
}

// Many wallets don't return a user-defined collateral in the set
// of UTxOs, yielding an edge case where users set a collateral but
// every other UTxOs have native assets and cannot be used for
// collateral. We therefore try to find and set the first collateral
// of the wallet.
export async function addHeliosCollateral(
  wallet: helios.Cip30Wallet,
  tx: helios.Tx
) {
  // The API is experimental in certain wallets. We should not
  // block the transaction building if a wallet doesn't suppport it.
  // And pray that the edge case doesn't hit instead...
  try {
    const walletCollateral = await wallet.collateral;
    tx.addCollateral(walletCollateral[0]);
  } catch (e) {
    console.warn(e);
  }
}
